import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ReactNode } from "react";
import { lightScrollbarStyle } from "../../../../../../styles/lightScrollbarStyle";
import { CompanySpend } from "../../../../../../legacy/queries/Companies";

const hasNoSpendEstimate = (
  rows: CompanySpend[] | undefined,
  rootName?: string
) =>
  rootName
    ? rows?.find((row) => row.category.name === rootName)?.absoluteValue == null
    : !rows?.length || rows.every((row) => row.absoluteValue == null);

const SpendCardMessage = ({
  color,
  testId,
  children,
}: {
  color: string;
  testId?: string;
  children: ReactNode;
}) => (
  <Stack alignItems="center" justifyContent="center" height={380}>
    <Typography color={color} variant="body2" data-testid={testId}>
      {children}
    </Typography>
  </Stack>
);

interface SpendCardBodyProps {
  loading: boolean;
  error: boolean;
  emptyMessage?: string;
  testIdPrefix: string;
  rows: CompanySpend[] | undefined;
  rootName?: string;
  children: ReactNode;
}

const SpendCardState = ({
  loading,
  error,
  emptyMessage,
  testIdPrefix,
  rows,
  rootName,
  children,
}: SpendCardBodyProps) => {
  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={380} />;
  }
  if (error) {
    return (
      <SpendCardMessage color="error" testId={`${testIdPrefix}-error`}>
        Error loading data.
      </SpendCardMessage>
    );
  }
  if (emptyMessage && hasNoSpendEstimate(rows, rootName)) {
    return (
      <SpendCardMessage color="text.secondary" testId={`${testIdPrefix}-empty`}>
        {emptyMessage}
      </SpendCardMessage>
    );
  }
  return children;
};

export const SpendCardBody = (props: SpendCardBodyProps) => (
  <Box flex={1} minHeight={0} overflow="auto" pr={2} sx={lightScrollbarStyle}>
    <SpendCardState {...props} />
  </Box>
);

export const SpendCardTitle = ({
  title,
  caption,
  testId,
}: {
  title: string;
  caption?: string;
  testId: string;
}) => (
  <Box>
    <Typography variant="h4" fontWeight="bold">
      {title}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary" data-testid={testId}>
        {caption}
      </Typography>
    )}
  </Box>
);
